import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SectionFrame } from "../atoms/SectionFrame";
import { FadeIn } from "../atoms/FadeIn";

// ──────────────────────────────────────────────────────────────
// Single swappable accent — swap "#D8FF3D" → "#FFB020" for amber.
// ──────────────────────────────────────────────────────────────
const ACCENT = "#D8FF3D";
const ACCENT_SOFT = "rgba(216, 255, 61, 0.35)";

const LABELS = [
    "Product explainers",
    "Employee onboarding",
    "Internal comms",
    "Training modules",
    "Launch films",
    "Investor decks",
];

// 2-D elliptical rings — different semi-axes + 2-D rotations give the
// impression of tilted orbits in 3-D space without distorting labels.
//   rx, ry         : ellipse semi-axes (px)
//   rot            : 2-D rotation of the ellipse (deg)
//   period         : full orbit duration (s)
//   dir            : +1 / -1
//   nodes          : LABELS[] indices placed on this ring
const DESKTOP_RINGS = [
    { rx: 230, ry: 72, rot: -6, period: 30, dir: 1, nodes: [0, 1] },
    { rx: 310, ry: 102, rot: 14, period: 46, dir: -1, nodes: [2, 3] },
    { rx: 390, ry: 130, rot: -18, period: 64, dir: 1, nodes: [4, 5] },
];
const MOBILE_RINGS = [
    { rx: 150, ry: 50, rot: -8, period: 34, dir: 1, nodes: [0, 1, 2] },
    { rx: 220, ry: 78, rot: 12, period: 52, dir: -1, nodes: [3, 4, 5] },
];

function usePrefersReducedMotion() {
    const [r, setR] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setR(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
        // Mount-once: mq and update are created inside the effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return r;
}

function useMediaQuery(query) {
    const [m, setM] = useState(() => {
        if (typeof window === "undefined" || !window.matchMedia) return false;
        return window.matchMedia(query).matches;
    });
    useEffect(() => {
        const mq = window.matchMedia(query);
        const update = () => setM(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, [query]);
    return m;
}

export function AudienceSection() {
    const reduced = usePrefersReducedMotion();
    const isDesktop = useMediaQuery("(min-width: 768px)");
    const rings = isDesktop ? DESKTOP_RINGS : MOBILE_RINGS;

    return (
        <SectionFrame innerClassName="">
            <div
                data-testid="audience-section"
                className="relative w-full min-h-[760px] md:min-h-screen overflow-hidden flex flex-col items-center justify-start px-6 md:px-10 pt-20 md:pt-28 pb-12"
                style={{ background: "#0a0b0a" }}
            >
                {/* Headline — dominant, no faded text behind */}
                <FadeIn y={30} className="relative z-30 text-center max-w-[920px]">
                    <div
                        className="text-sm sm:text-base uppercase tracking-[0.32em] mb-4 md:mb-6"
                        style={{ color: "rgba(255,255,255,0.5)" }}
                    >
                        Who we serve
                    </div>
                    <h2
                        data-testid="audience-center-orb"
                        className="display-text font-black uppercase"
                        style={{
                            fontSize: "clamp(2rem, 5.6vw, 4.5rem)",
                            lineHeight: 1.02,
                            letterSpacing: "-0.025em",
                        }}
                    >
                        Businesses that need<br className="hidden sm:block" /> to explain.
                    </h2>
                </FadeIn>

                {/* Orbital stage */}
                <OrbitalStage rings={rings} isDesktop={isDesktop} reduced={reduced} />
            </div>
        </SectionFrame>
    );
}

// Mouse parallax — smoothly tilts the stage toward the cursor.
function useParallax(stageRef, reduced) {
    const [parallax, setParallax] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const node = stageRef.current;
        if (!node || reduced) return;
        let targetX = 0;
        let targetY = 0;
        let raf;
        const onMove = (e) => {
            const rect = node.getBoundingClientRect();
            const nx = (e.clientX - rect.left) / rect.width - 0.5;
            const ny = (e.clientY - rect.top) / rect.height - 0.5;
            targetX = ny * 8;
            targetY = -nx * 8;
        };
        const onLeave = () => {
            targetX = 0;
            targetY = 0;
        };
        const tick = () => {
            setParallax((p) => ({
                x: p.x + (targetX - p.x) * 0.08,
                y: p.y + (targetY - p.y) * 0.08,
            }));
            raf = requestAnimationFrame(tick);
        };
        node.addEventListener("mousemove", onMove);
        node.addEventListener("mouseleave", onLeave);
        raf = requestAnimationFrame(tick);
        return () => {
            node.removeEventListener("mousemove", onMove);
            node.removeEventListener("mouseleave", onLeave);
            cancelAnimationFrame(raf);
        };
        // stageRef is a stable ref; listeners/locals are created inside the effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduced]);

    return parallax;
}

function OrbitalStage({ rings, isDesktop, reduced }) {
    const stageRef = useRef(null);
    const [hovered, setHovered] = useState(null); // { ringIdx, slot } | null
    const parallax = useParallax(stageRef, reduced);

    // Stage dimensions — based on outermost ring
    const STAGE_W = isDesktop ? 900 : 560;
    const STAGE_H = isDesktop ? 520 : 360;
    const cx = STAGE_W / 2;
    const cy = STAGE_H / 2;

    return (
        <div
            ref={stageRef}
            className="relative mt-8 md:mt-10"
            style={{
                width: "min(96vw, " + STAGE_W + "px)",
                height: "min(74vw, " + STAGE_H + "px)",
                perspective: "1400px",
            }}
        >
            <motion.div
                className="absolute inset-0 origin-center"
                style={{
                    transformStyle: "preserve-3d",
                    transform: `rotateX(${parallax.x}deg) rotateY(${parallax.y}deg)`,
                    willChange: "transform",
                }}
            >
                {/* Soft lime glow behind globe */}
                <div
                    className="absolute pointer-events-none"
                    style={{
                        left: "50%",
                        top: "50%",
                        width: isDesktop ? 360 : 240,
                        height: isDesktop ? 360 : 240,
                        transform: "translate(-50%, -50%)",
                        background: `radial-gradient(circle at 50% 50%, ${ACCENT_SOFT} 0%, rgba(216,255,61,0.06) 40%, transparent 72%)`,
                        filter: "blur(36px)",
                    }}
                />

                {/* Single SVG plane — ring tracks, connection line, globe, dots */}
                <svg
                    viewBox={`0 0 ${STAGE_W} ${STAGE_H}`}
                    className="absolute inset-0 w-full h-full"
                    preserveAspectRatio="xMidYMid meet"
                >
                    {/* Ring tracks */}
                    {rings.map((ring, i) => (
                        <ellipse
                            key={`t-${i}`}
                            cx={cx}
                            cy={cy}
                            rx={ring.rx}
                            ry={ring.ry}
                            transform={`rotate(${ring.rot} ${cx} ${cy})`}
                            fill="none"
                            stroke="rgba(255,255,255,0.06)"
                            strokeWidth="1"
                            strokeDasharray="2 4"
                        />
                    ))}

                    {/* Wireframe globe */}
                    <GlobeSVG cx={cx} cy={cy} r={isDesktop ? 90 : 70} reduced={reduced} />
                </svg>

                {/* Labels — HTML layer above SVG, positioned by JS, always upright */}
                <LabelsLayer
                    rings={rings}
                    cx={cx}
                    cy={cy}
                    hovered={hovered}
                    setHovered={setHovered}
                    reduced={reduced}
                />
            </motion.div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────
// Wireframe globe — static SVG sphere with parallels & meridians,
// plus a slow accent equator highlight that "drifts" to suggest rotation.
// ──────────────────────────────────────────────────────────────
function GlobeSVG({ cx, cy, r, reduced }) {
    const STROKE = "rgba(255,255,255,0.22)";
    const STROKE_DIM = "rgba(255,255,255,0.10)";

    const parallels = [-72, -54, -36, -18, 18, 36, 54, 72].map((y) => {
        const rx = Math.sqrt(Math.max(0, 100 * 100 - y * y));
        return { y, rx };
    });

    // Meridian count
    const meridians = [0, 25, 50, 75, 100, 125, 150];

    return (
        <g transform={`translate(${cx} ${cy}) scale(${r / 100})`}>
            {/* outline */}
            <circle cx="0" cy="0" r="100" fill="none" stroke={STROKE} strokeWidth="1.2" />
            {/* parallels */}
            {parallels.map((p, i) => (
                <ellipse
                    key={`p-${i}`}
                    cx="0"
                    cy={p.y}
                    rx={p.rx}
                    ry={p.rx * 0.16}
                    fill="none"
                    stroke={STROKE_DIM}
                    strokeWidth="1"
                />
            ))}
            {/* meridians */}
            {meridians.map((deg, i) => {
                const rx = 100 * Math.abs(Math.cos((deg * Math.PI) / 180));
                return (
                    <ellipse
                        key={`m-${i}`}
                        cx="0"
                        cy="0"
                        rx={Math.max(2, rx)}
                        ry={100}
                        fill="none"
                        stroke={STROKE_DIM}
                        strokeWidth="1"
                    />
                );
            })}
            {/* equator emphasis */}
            <ellipse cx="0" cy="0" rx="100" ry="14" fill="none" stroke={STROKE} strokeWidth="1.3" />
            {/* drifting accent arc — gives the illusion of rotation without distorting the sphere */}
            <ellipse
                cx="0"
                cy="0"
                rx="100"
                ry="14"
                fill="none"
                stroke={ACCENT}
                strokeWidth="1.2"
                strokeDasharray="40 220"
                style={{
                    animation: reduced ? "none" : "audience-globe-drift 18s linear infinite",
                    opacity: 0.7,
                }}
            />
            {/* poles */}
            <circle cx="0" cy="-100" r="1.8" fill={ACCENT} opacity="0.7" />
            <circle cx="0" cy="100" r="1.8" fill={ACCENT} opacity="0.7" />
            <style>{`
                @keyframes audience-globe-drift {
                    from { stroke-dashoffset: 0; }
                    to   { stroke-dashoffset: -260; }
                }
            `}</style>
        </g>
    );
}

// ──────────────────────────────────────────────────────────────
// Compute a node's (x,y) on a rotated ellipse at angle t (radians).
// ──────────────────────────────────────────────────────────────
function pointOnEllipse(cx, cy, ring, t) {
    const x0 = ring.rx * Math.cos(t);
    const y0 = ring.ry * Math.sin(t);
    const rad = (ring.rot * Math.PI) / 180;
    const cosR = Math.cos(rad);
    const sinR = Math.sin(rad);
    return {
        x: cx + x0 * cosR - y0 * sinR,
        y: cy + x0 * sinR + y0 * cosR,
    };
}

// ──────────────────────────────────────────────────────────────
// Shared JS-RAF angle store — each ring has its own angle.
// Pause angle progression when a node on this ring is hovered.
// ──────────────────────────────────────────────────────────────
function useRingAngles(rings, hovered, reduced) {
    const [angles, setAngles] = useState(() => rings.map(() => 0));
    const anglesRef = useRef(angles);
    useEffect(() => { anglesRef.current = angles; }, [angles]);

    // Resync angles length whenever the rings count changes (e.g. resize across breakpoints)
    useEffect(() => {
        if (anglesRef.current.length !== rings.length) {
            const synced = rings.map((_, i) => anglesRef.current[i] ?? 0);
            anglesRef.current = synced;
            setAngles(synced);
        }
    }, [rings.length]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (reduced) return;
        let raf;
        let last = performance.now();
        const step = (now) => {
            const dt = (now - last) / 1000;
            last = now;
            const next = rings.map((ring, i) => {
                const a = anglesRef.current[i] ?? 0;
                if (!ring) return a;
                const isPaused = hovered && hovered.ringIdx === i;
                if (isPaused) return a;
                const delta = (dt / ring.period) * 2 * Math.PI * ring.dir;
                return a + delta;
            });
            anglesRef.current = next;
            setAngles(next);
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [rings, hovered, reduced]);

    return angles;
}

// ──────────────────────────────────────────────────────────────
// SVG layer — dots + (on hover) thin lime line back to globe centre.
// (Dots are now drawn inside LabelsLayer for single-frame consistency.)
// ──────────────────────────────────────────────────────────────
// LabelsLayer also needs angles — so we host them in a single Nodes
// component that takes rings + hovered, computes once per frame.
// ──────────────────────────────────────────────────────────────
// Geometry for one orbiting node.
function nodeAt(ring, ringIdx, slot, angles, cx, cy, hovered) {
    const base = (slot / ring.nodes.length) * 2 * Math.PI;
    const t = base + angles[ringIdx];
    const p = pointOnEllipse(cx, cy, ring, t);
    const isHovered = !!(hovered && hovered.ringIdx === ringIdx && hovered.slot === slot);
    return { p, isHovered };
}

function NodeDot({ p, isHovered, cx, cy }) {
    return (
        <g>
            {isHovered && (
                <line
                    x1={cx}
                    y1={cy}
                    x2={p.x}
                    y2={p.y}
                    stroke={ACCENT}
                    strokeWidth="1.2"
                    opacity="0.85"
                    style={{ filter: `drop-shadow(0 0 6px ${ACCENT_SOFT})` }}
                />
            )}
            <circle
                cx={p.x}
                cy={p.y}
                r={isHovered ? 5 : 3.2}
                fill={ACCENT}
                style={{
                    filter: isHovered
                        ? `drop-shadow(0 0 10px ${ACCENT}) drop-shadow(0 0 24px ${ACCENT_SOFT})`
                        : `drop-shadow(0 0 4px ${ACCENT_SOFT})`,
                    transition: "r 0.25s ease-out",
                }}
            />
        </g>
    );
}

function NodeLabel({ p, isHovered, labelIdx, cx, cy, onEnter, onLeave }) {
    const placeRight = p.x >= cx;
    return (
        <button
            type="button"
            data-testid={`audience-node-${LABELS[labelIdx].toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            onFocus={onEnter}
            onBlur={onLeave}
            className="absolute pointer-events-auto inline-flex items-center gap-2 px-2 py-1 -translate-y-1/2"
            style={{
                left: `${(p.x / (cx * 2)) * 100}%`,
                top: `${(p.y / (cy * 2)) * 100}%`,
                transform: `translate(${placeRight ? "8px" : "calc(-100% - 8px)"}, -50%)`,
                whiteSpace: "nowrap",
            }}
        >
            <span
                className="text-sm sm:text-base uppercase tracking-[0.22em]"
                style={{
                    color: isHovered ? "#ECECEC" : "rgba(255,255,255,0.55)",
                    textShadow: isHovered ? `0 0 12px ${ACCENT_SOFT}` : "none",
                    transition: "color 0.25s ease-out, text-shadow 0.25s ease-out",
                    fontFamily: "'Glacial Indifference', sans-serif",
                }}
            >
                {LABELS[labelIdx]}
            </span>
        </button>
    );
}

function LabelsLayer({ rings, cx, cy, hovered, setHovered, reduced }) {
    const angles = useRingAngles(rings, hovered, reduced);

    return (
        <>
            {/* SVG layer for dots + connection lines */}
            <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox={`0 0 ${cx * 2} ${cy * 2}`}
                preserveAspectRatio="xMidYMid meet"
            >
                {rings.flatMap((ring, ringIdx) =>
                    ring.nodes.map((labelIdx, slot) => {
                        const { p, isHovered } = nodeAt(ring, ringIdx, slot, angles, cx, cy, hovered);
                        return <NodeDot key={`d-${ringIdx}-${slot}`} p={p} isHovered={isHovered} cx={cx} cy={cy} />;
                    }),
                )}
            </svg>

            {/* HTML labels — always upright, hit-test handled here */}
            <div className="absolute inset-0 pointer-events-none">
                {rings.flatMap((ring, ringIdx) =>
                    ring.nodes.map((labelIdx, slot) => {
                        const { p, isHovered } = nodeAt(ring, ringIdx, slot, angles, cx, cy, hovered);
                        return (
                            <NodeLabel
                                key={`l-${ringIdx}-${slot}`}
                                p={p}
                                isHovered={isHovered}
                                labelIdx={labelIdx}
                                cx={cx}
                                cy={cy}
                                onEnter={() => setHovered({ ringIdx, slot })}
                                onLeave={() => setHovered(null)}
                            />
                        );
                    }),
                )}
            </div>
        </>
    );
}
